import { ReactNode, useEffect } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import { MeHealthCenter, Mode } from '../api/me'
import { useMe } from '../context/MeContext'
import { ConfirmProvider } from './common'
import { ToastProvider } from './common'
import { Navbar } from './Navbar'
import { getModeColor, resolveMode } from '../lib/mode'
import { registerSuperProperties, setProfileProperties } from '../lib/config'

export function useCurrentHealthCenter(): MeHealthCenter | null {
  const me = useMe()
  const params = useParams()
  const id = Number(params.healthCenterId)
  return me.health_centers.find((healthCenter) => healthCenter.id === id) ?? null
}

export function useIsAccountClosed(): boolean {
  return useCurrentHealthCenter()?.account_state === 'closed'
}

export function useCurrentMode(): Mode | null {
  const healthCenter = useCurrentHealthCenter()
  const [searchParams] = useSearchParams()
  if (!healthCenter) {
    return null
  }
  return resolveMode(healthCenter, searchParams.get('mode'))
}

/** `modeOverride` stands in for a controller that pins @current_mode instead of reading it from params. */
export function AppShell({ children, modeOverride }: { children: ReactNode; modeOverride?: Mode }) {
  const me = useMe()
  const healthCenter = useCurrentHealthCenter()
  const resolvedMode = useCurrentMode()
  const mode = modeOverride ?? resolvedMode

  // Runs in the commit before PageViewTracker's deferred emit, so the first SPA Page Viewed
  // already carries the plan the professional is on.
  useEffect(() => {
    registerSuperProperties({
      health_center_id: healthCenter?.id,
      account_type: healthCenter?.account_type,
      account_state: healthCenter?.account_state,
      has_current_paid_payment: healthCenter?.has_current_paid_payment,
      profession_key: healthCenter?.profession.key,
      is_administrator: healthCenter?.administrator,
      mode,
      health_center_count: me.health_centers.length,
    })
    if (!healthCenter) {
      return
    }
    setProfileProperties({
      last_health_center_id: healthCenter.id,
      account_type: healthCenter.account_type,
      account_state: healthCenter.account_state,
      patients_count: healthCenter.patients_count,
      profession_key: healthCenter.profession.key,
    })
  }, [healthCenter?.id, mode])

  // Redirecting during render rather than from an effect: an effect would paint the patients
  // page first, and window.location.assign would then discard the tree and refetch /api/me.
  if (healthCenter && mode === 'calendar' && !window.location.pathname.includes('/box_calendars')) {
    return <Navigate to={`/health_centers/${healthCenter.id}/box_calendars`} replace />
  }

  return (
    <ConfirmProvider>
      <ToastProvider>
        {mode && <style>{`.panel .panel-heading { background-color: ${getModeColor(mode)}; }`}</style>}
        <Navbar healthCenter={healthCenter} currentMode={mode} />
        <div id="div_for_yield" data-testid="app-ready" className="no-margin-xs no-padding-xs container-fluid" style={{ width: '100%' }}>
          {children}
        </div>
      </ToastProvider>
    </ConfirmProvider>
  )
}
